import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Target, Flame, Droplet, Apple, Moon, Eye, Brain, Droplets } from 'lucide-react';
import AffirmationsModule from './AffirmationsModule';
import VisualizationModule from './VisualizationModule';
import HydrationTracker from '../HydrationTracker';
import NutritionTracker from '../NutritionTracker';
import SleepTracker from '../SleepTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DailyPromptCard = ({ prompt, onSubmit, userTeams = [], setConfirmDialog }) => {
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedAffirmationData, setSelectedAffirmationData] = useState(null);

  const loadSelectedAffirmation = async () => {
    try {
      const response = await axios.get(`${API}/affirmations/selected`);
      if (response.data.selected) {
        setSelectedAffirmationData(response.data.selection);
      }
    } catch (error) {
      console.error('Failed to load selected affirmation:', error.message);
    }
  };

  // Load selected affirmation on mount for affirmations category
  useEffect(() => {
    if (prompt.category === 'affirmations') {
      const timer = setTimeout(() => {
        loadSelectedAffirmation();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [prompt.category]);

  const selectAffirmation = async (affirmation) => {
    try {
      setLoading(true);
      
      // Get user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      const response = await axios.post(`${API}/affirmations/select`, {
        affirmation_id: affirmation.id,
        affirmation_text: affirmation.text,
        local_timezone: userTimezone,
        notification_enabled: true
      });
      
      setSelectedAffirmationData({
        affirmation_text: affirmation.text,
        selected_at: new Date().toISOString(),
        notification_enabled: true
      });
      
      // Mark the affirmations challenge as complete
      await onSubmit(prompt.id, "Daily affirmations read and internalized", "affirmations");
      
      toast.success('Affirmation selected!');
      
      // Schedule local notifications as fallback
      scheduleLocalNotifications(affirmation.text, userTimezone);
      
    } catch (error) {
      toast.error('Failed to select affirmation');
    } finally {
      setLoading(false);
    }
  };

  const clearSelectedAffirmation = async () => {
    try {
      setLoading(true);
      await axios.delete(`${API}/affirmations/selected`);
      setSelectedAffirmationData(null);
      
      // Clear scheduled notifications
      clearScheduledNotifications();
      
      toast.success('Affirmation selection cleared');
    } catch (error) {
      toast.error('Failed to clear selection');
    } finally {
      setLoading(false);
    }
  };

  const scheduleLocalNotifications = (affirmationText, timezone) => {
    // Clear any existing timeouts
    clearScheduledNotifications();
    
    if (Notification.permission !== 'granted') {
      return;
    }

    const now = new Date();
    const endTime = new Date();
    endTime.setHours(21, 0, 0, 0); // 9 PM today
    
    // If it's already past 9 PM, don't schedule notifications
    if (now.getHours() >= 21) {
      return;
    }

    const scheduleNext = (delay) => {
      const timeoutId = setTimeout(() => {
        const currentTime = new Date();
        
        // Check if we're still before 9 PM
        if (currentTime.getHours() < 21) {
          // Show notification
          if (Notification.permission === 'granted') {
            new Notification('Affirmation Reminder', {
              body: `"${affirmationText}" - Take a moment to repeat this to yourself.`,
              icon: '/favicon.ico',
              tag: 'affirmation-reminder'
            });
          }
          
          // Schedule next notification (73 minutes later)
          const nextDelay = 73 * 60 * 1000; // 73 minutes in milliseconds
          const nextTime = new Date(currentTime.getTime() + nextDelay);
          
          // Only schedule if next notification would be before 9 PM
          if (nextTime.getHours() < 21) {
            scheduleNext(nextDelay);
          }
        }
      }, delay);
      
      // Store timeout ID for cleanup
      const timeouts = JSON.parse(localStorage.getItem('affirmation_timeouts') || '[]');
      timeouts.push(timeoutId);
      localStorage.setItem('affirmation_timeouts', JSON.stringify(timeouts));
    };

    // Schedule first notification in 73 minutes
    const initialDelay = 73 * 60 * 1000; // 73 minutes
    scheduleNext(initialDelay);
  };

  const clearScheduledNotifications = () => {
    const timeouts = JSON.parse(localStorage.getItem('affirmation_timeouts') || '[]');
    timeouts.forEach(timeoutId => clearTimeout(timeoutId));
    localStorage.removeItem('affirmation_timeouts');
  };

  const handleSubmit = async () => {
    if (!response.trim()) return;
    
    setLoading(true);
    const success = await onSubmit(prompt.id, response, prompt.category);
    if (success) {
      setResponse('');
    }
    setLoading(false);
  };

  const categoryIcons = {
    affirmations: Brain,
    visualization: Eye,
    nutrition: Apple,
    hydration: Droplets,
    sleep: () => <span className="text-2xl">💤</span>
  };

  const Icon = categoryIcons[prompt.category] || Target;

  return (
    <Card className="h-full border-0 shadow-lg bg-blue-900/95 backdrop-blur-sm border border-blue-700">
      <CardHeader className="pb-3">
        <div className="flex items-center space-x-2">
          <Icon className="w-5 h-5 text-yellow-400" />
          <CardTitle className="text-lg capitalize text-white">{prompt.category}</CardTitle>
          {prompt.completed_today && (
            <Badge variant="secondary" className="bg-yellow-500 text-blue-900 font-semibold">
              Completed
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Only show prompt text for non-affirmation, non-hydration, and non-sleep categories */}
        {prompt.category !== 'affirmations' && prompt.category !== 'hydration' && prompt.category !== 'sleep' && (
          <p className="text-slate-50">{prompt.prompt_text}</p>
        )}
        
        {/* Special handling for affirmations */}
        {prompt.category === 'affirmations' ? (
          <AffirmationsModule
            selectedAffirmationData={selectedAffirmationData}
            userTeams={userTeams}
            onSelect={selectAffirmation}
            onClear={clearSelectedAffirmation}
            setConfirmDialog={setConfirmDialog}
          />
        ) : prompt.category === 'visualization' ? (
          <VisualizationModule
            completedToday={prompt.completed_today}
            onComplete={async (message) => {
              setLoading(true);
              await onSubmit(prompt.id, message, "visualization");
              setLoading(false);
            }}
          />
        ) : prompt.category === 'hydration' ? (
          <HydrationTracker onComplete={onSubmit} promptId={prompt.id} />
        ) : prompt.category === 'nutrition' ? (
          <NutritionTracker onComplete={onSubmit} promptId={prompt.id} />
        ) : prompt.category === 'sleep' ? (
          <SleepTracker onComplete={onSubmit} promptId={prompt.id} />
        ) : (
          /* Regular prompt handling for other categories */
          !prompt.completed_today && (
            <>
              <Textarea
                placeholder="Share your thoughts..."
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                className="border-blue-700 focus:border-yellow-500"
                rows={4}
              />
              <Button 
                onClick={handleSubmit}
                disabled={loading || !response.trim()}
                className="w-full bg-gradient-to-r from-blue-900 to-yellow-500 hover:from-blue-950 hover:to-yellow-600"
              >
                {loading ? 'Submitting...' : 'Complete Challenge'}
              </Button>
            </>
          )
        )}
      </CardContent>
    </Card>
  );
};

export default DailyPromptCard;
